import { SweepMesh } from '../geometry/sweep';

const AREA_EPS = 1e-18; // m² — drop degenerate triangles before scale
const COORD_LIMIT = 1e6; // absurd world-metres guard

export interface MeshCleanupStats {
  inputTriangles: number;
  outputTriangles: number;
  droppedDegenerate: number;
  droppedNonFinite: number;
}

/**
 * Rebuild a mesh for STL export: geometric facet normals via right-hand winding,
 * drop degenerate / non-finite triangles. Does not boolean-union overlaps.
 */
export function cleanupMeshForStl(mesh: SweepMesh): { mesh: SweepMesh; stats: MeshCleanupStats } {
  const { positions, indices } = mesh;
  const inputTriangles = Math.floor(indices.length / 3);
  const outPos: number[] = [];
  const outNorm: number[] = [];
  const outIdx: number[] = [];
  let droppedDegenerate = 0;
  let droppedNonFinite = 0;

  for (let t = 0; t < inputTriangles; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];
    const ax = positions[i0 * 3];
    const ay = positions[i0 * 3 + 1];
    const az = positions[i0 * 3 + 2];
    const bx = positions[i1 * 3];
    const by = positions[i1 * 3 + 1];
    const bz = positions[i1 * 3 + 2];
    const cx = positions[i2 * 3];
    const cy = positions[i2 * 3 + 1];
    const cz = positions[i2 * 3 + 2];

    if (
      !Number.isFinite(ax) ||
      !Number.isFinite(ay) ||
      !Number.isFinite(az) ||
      !Number.isFinite(bx) ||
      !Number.isFinite(by) ||
      !Number.isFinite(bz) ||
      !Number.isFinite(cx) ||
      !Number.isFinite(cy) ||
      !Number.isFinite(cz) ||
      Math.abs(ax) > COORD_LIMIT ||
      Math.abs(ay) > COORD_LIMIT ||
      Math.abs(az) > COORD_LIMIT ||
      Math.abs(bx) > COORD_LIMIT ||
      Math.abs(by) > COORD_LIMIT ||
      Math.abs(bz) > COORD_LIMIT ||
      Math.abs(cx) > COORD_LIMIT ||
      Math.abs(cy) > COORD_LIMIT ||
      Math.abs(cz) > COORD_LIMIT
    ) {
      droppedNonFinite += 1;
      continue;
    }

    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    // |cross| = 2 * triangle area
    if (!(len > AREA_EPS)) {
      droppedDegenerate += 1;
      continue;
    }
    nx /= len;
    ny /= len;
    nz /= len;

    const base = outPos.length / 3;
    outPos.push(ax, ay, az, bx, by, bz, cx, cy, cz);
    outNorm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    outIdx.push(base, base + 1, base + 2);
  }

  return {
    mesh: {
      positions: Float32Array.from(outPos),
      normals: Float32Array.from(outNorm),
      indices: Uint32Array.from(outIdx),
    },
    stats: {
      inputTriangles,
      outputTriangles: outIdx.length / 3,
      droppedDegenerate,
      droppedNonFinite,
    },
  };
}

/** Geometric unit normal for triangle (i0,i1,i2), or null if degenerate. */
export function triangleNormal(
  positions: Float32Array,
  i0: number,
  i1: number,
  i2: number,
): [number, number, number] | null {
  const ax = positions[i0 * 3];
  const ay = positions[i0 * 3 + 1];
  const az = positions[i0 * 3 + 2];
  const bx = positions[i1 * 3];
  const by = positions[i1 * 3 + 1];
  const bz = positions[i1 * 3 + 2];
  const cx = positions[i2 * 3];
  const cy = positions[i2 * 3 + 1];
  const cz = positions[i2 * 3 + 2];
  const ux = bx - ax;
  const uy = by - ay;
  const uz = bz - az;
  const vx = cx - ax;
  const vy = cy - ay;
  const vz = cz - az;
  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz);
  if (!(len > AREA_EPS)) return null;
  return [nx / len, ny / len, nz / len];
}
